import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useSaveUserAsContact } from '#app/hooks/useSaveUserAsContact.js';
import { useUserListMutations } from '../hooks/useUserListMutations.js';
import { AddToListMenuPrimitive } from './AddToListMenuPrimitive.js';
import { type AddToListMenuReturn } from './useAddToListMenu.js';
import { useAddToListMutations } from './useAddToListMutations.js';
import {
  CreateContactForUserProfileSource,
  UpdateUsersMembershipsInUserListsInput,
} from '@contra/graphql-types';
import { captureException } from '@contra/sentry/react';
import { ReactNode, useCallback, useMemo, useState } from 'react';

type UnauthenticatedAddToListMenuProps = {
  readonly addToListMenuProps: AddToListMenuReturn;
  readonly analyticsSection: string;
  readonly placeholderListName: string;
  readonly source: CreateContactForUserProfileSource;
  readonly trigger: ReactNode;
  readonly userProfile: {
    avatar: null | {
      id: string;
      uid: string;
      url: string;
    };
    firstName: string;
    id: string;
    isSavedContact: boolean;
  };
};

export const UnauthenticatedAddToListMenu = ({
  addToListMenuProps,
  analyticsSection,
  placeholderListName,
  source,
  trigger,
  userProfile,
}: UnauthenticatedAddToListMenuProps) => {
  const toast = useToast();

  const isSaved = userProfile.isSavedContact;

  const [checkedListIds, setCheckedListIds] = useState(new Set<string>([]));

  const lists = useMemo(
    () => [
      {
        id: 'placeholder-list-1',
        isExpertList: false,
        name: placeholderListName,
      },
    ],
    [placeholderListName],
  );

  const selectedLists = lists.filter((list) => checkedListIds.has(list.id));

  const { updateListMemberships } = useAddToListMutations();

  const handleUpdateListMemberships = useCallback(
    ({ input }: { input: UpdateUsersMembershipsInUserListsInput }) => {
      updateListMemberships({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              captureException(payloadErrors[0]);
              toast.error(
                'Sorry, something went wrong. Please try again or get in touch with support for assistance.',
              );
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            toast.success(
              `${userProfile.firstName} was saved to ${placeholderListName}`,
            );
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input,
          userProfileId: userProfile.id,
        },
      });
    },
    [
      placeholderListName,
      toast,
      updateListMemberships,
      userProfile.firstName,
      userProfile.id,
    ],
  );

  const { createUserList } = useUserListMutations();

  const { saveUserAsContact } = useSaveUserAsContact({
    analyticsSection,
    contactId: null,
    source,
    userProfile: {
      ...userProfile,
      // this is not used in V2, can be removed from the hook later
      avatarImage: null,
      fullName: '',
    },
  });

  const onNewListCreated = useCallback(
    (name: string) => {
      createUserList({
        name,
        onSuccess: (listId) => {
          handleUpdateListMemberships({
            input: {
              userListIdsToAddTo: [listId],
              userListIdsToRemoveFrom: [],
              userProfileIds: [userProfile.id],
            },
          });
        },
      });
    },
    [createUserList, handleUpdateListMemberships, userProfile.id],
  );

  const {
    closeModal: closeSaveUserSignUpModal,
    openModal: openSaveUserSignUpModal,
  } = useModal({
    identifier: 'SaveUserSignUpModal',
    modalProps: {
      listName: placeholderListName,
      onSignUpCompleted: () => {
        saveUserAsContact();
        onNewListCreated(placeholderListName);
        closeSaveUserSignUpModal();
      },
      userProfileToSave: userProfile,
    },
  });

  const isMobile = useIsMobile();

  const onSelectedItemChange = useCallback(() => {
    setCheckedListIds(new Set([lists[0].id]));

    if (isMobile) {
      addToListMenuProps.handleCloseListMenu();
    }

    openSaveUserSignUpModal();
  }, [addToListMenuProps, isMobile, lists, openSaveUserSignUpModal]);

  return (
    <AddToListMenuPrimitive
      addToListMenuProps={addToListMenuProps}
      analyticsSection={analyticsSection}
      availableLists={lists}
      disableSearchAndCreateInput
      isModalOpen={false}
      isSaved={isSaved}
      onListDeleted={() => {}}
      onListNameUpdated={() => {}}
      onNewListCreated={() => {}}
      onSelectedItemChange={onSelectedItemChange}
      onUnsave={() => {}}
      selectedLists={selectedLists}
      trigger={trigger}
    />
  );
};
